import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useFormikContext } from "formik";
import { getRegions } from "@qgiv/core-js";
import {
    FormikField,
    FormikCheckbox,
    FormikSelect,
    CountryOptions,
    RegionOptions,
} from "@qgiv/core-donor";
import ConnectedMailingAddressPreview from "../../common/ConnectedMailingAddressPreview";
import ConnectedPhonePreview from "./ConnectedPhonePreview";
import ConnectedEmailRegistrationField from "./ConnectedEmailRegistrationField";

const ParticipantDetails = ({
    sectionHeading = "Let’s get your details.",
    sectionHeadingForAdditionalParticipants = "Let’s get their details.",
    additionalText = "",
    additionalTextForAdditionalParticipants = "",
    firstNameLabel = "First Name",
    lastNameLabel = "Last Name",
    emailLabel = "Email",
    emailDescription = "We’ll send you a confirmation, plus a link to set up your fundraising page!",
    emailDescriptionForAdditionalNonFundraisingParticipant = "If you include an email, we’ll send them a copy of the confirmation.",
    emailDescriptionForAdditionalParticipant = "We’ll send them a link to set up their fundraising page!",
    emailDescriptionForNonFundraisingParticipant = "We’ll send you a confirmation.",
    addressLine1Label = "Address",
    addressLine2Label = "Apartment, Suite, Etc.",
    cityLabel = "City",
    stateLabel = "State",
    zipCodeLabel = "Zip Code",
    countryLabel = "Country",
    phoneNumberLabel = "Phone",
    isChildParticipant = false,
    isManagedByPrimary = false,
    phoneInputOptional = "",
    shouldDisplayPhone = true,
    addressCheckboxLabelForAdditionalParticipants = "Use my address",
    phoneNumberCheckboxLabelForAdditionalParticipants = "Use my phone",
}) => {
    const {
        values: {
            Country,
            Is_Nfp = false,
            Use_Primary_Address = false,
            Use_Primary_Phone = false,
        },
    } = useFormikContext();
    const [regions, setRegions] = useState([]);
    const [heading, setHeading] = useState(sectionHeading);
    const [subheading, setSubheading] = useState(additionalText);
    const [emailDescriptionText, setEmailDescriptionText] =
        useState(emailDescription);
    const phoneRequired = phoneInputOptional.length > 0 ? "false" : "true";
    const stateZipRequired = Country === "US" ? "true" : "false";
    // If the phone number field is disabled or primary registrant
    // use primary phone checkbox should not be displayed.
    const usePrimaryPhoneCheckbox = (
        <div className="col col--12">
            <div className="grid">
                <div className="col col--12">
                    <FormikCheckbox
                        label="Use my phone"
                        name="Use_Primary_Phone"
                        type="checkbox"
                        id="Use-Primary-Phone"
                        helpText=""
                        innerPadding={false}
                    >
                        {phoneNumberCheckboxLabelForAdditionalParticipants}
                    </FormikCheckbox>
                </div>
            </div>
        </div>
    );

    useEffect(() => {
        let isComponentMounted = true;
        getRegions(Country).then((res) => {
            if (!isComponentMounted) return;
            setRegions(res.data);
        });
        // Cleanup function that is run when the component dismounts thus
        // preventing a memory leak by ensuring that the component state will
        // not update if the component has been dismounted by the time the
        // async request completes
        return () => {
            isComponentMounted = false;
        };
    }, [Country]);

    // The use my address is displayed to additional participants.
    const usePrimaryAddressCheckbox = (
        <div className="col col--12">
            <FormikCheckbox
                name="Use_Primary_Address"
                type="checkbox"
                id="Use-Primary-Address"
                helpText=""
                innerPadding={false}
            >
                {addressCheckboxLabelForAdditionalParticipants}
            </FormikCheckbox>
        </div>
    );

    const addressFields = (
        <>
            <div className="col col--12">
                <FormikField
                    label={addressLine1Label}
                    name="Address_1"
                    type="text"
                    id="Address-1"
                    ariaRequired="true"
                />
            </div>
            <div className="col col--12">
                <FormikField
                    label={addressLine2Label}
                    name="Address_2"
                    type="text"
                    id="Address-2"
                    descText="Optional"
                />
            </div>
            <div className="col col--12 col--lg-5">
                <FormikField
                    label={cityLabel}
                    name="City"
                    type="text"
                    id="City"
                    ariaRequired="true"
                />
            </div>
            <div className="col col--12 col--lg-4">
                <FormikSelect
                    label={Country === "US" ? stateLabel : "State/Province"}
                    name="State"
                    type="select"
                    id="State"
                    ariaRequired={stateZipRequired}
                >
                    {regions && regions.length > 0 && (
                        <RegionOptions regions={regions} />
                    )}
                </FormikSelect>
            </div>
            <div className="col col--12 col--lg-3">
                <FormikField
                    label={Country === "US" ? zipCodeLabel : "Postal Code"}
                    name="Zip"
                    type={Country === "US" ? "zip" : "text"}
                    id="Zip"
                    ariaRequired={stateZipRequired}
                />
            </div>
            <div className="col col--12">
                <FormikSelect
                    label={countryLabel}
                    name="Country"
                    type="select"
                    id="Country"
                    ariaRequired="true"
                >
                    <CountryOptions />
                </FormikSelect>
            </div>
        </>
    );
    const phoneField = (
        <>
            {isChildParticipant && usePrimaryPhoneCheckbox}

            <div className="col col--12 col--sm-6">
                <FormikField
                    label={phoneNumberLabel}
                    name="Phone"
                    type="tel"
                    id="Phone"
                    descText={phoneInputOptional}
                    ariaRequired={phoneRequired}
                    country={Country}
                />
            </div>
        </>
    );

    const [usePrimaryRegUi, setUsePrimaryRegUi] = useState(null);
    const [addressUi, setAddressUi] = useState(addressFields);
    const [phoneUi, setPhoneUi] = useState(phoneField);
    const [emailOptional, setEmailOptional] = useState(false);

    useEffect(() => {
        setHeading(
            isChildParticipant
                ? sectionHeadingForAdditionalParticipants
                : sectionHeading,
        );
        setSubheading(
            isChildParticipant
                ? additionalTextForAdditionalParticipants
                : additionalText,
        );
        setEmailDescriptionText(() => {
            if (isChildParticipant && Is_Nfp) {
                return emailDescriptionForAdditionalNonFundraisingParticipant;
            }
            if (isChildParticipant && !Is_Nfp) {
                return emailDescriptionForAdditionalParticipant;
            }
            if (!isChildParticipant && Is_Nfp) {
                return emailDescriptionForNonFundraisingParticipant;
            }
            return emailDescription;
        });
        if (isChildParticipant && Is_Nfp) {
            setEmailOptional(true);
        } else if (isChildParticipant && !Is_Nfp) {
            // If NFP is checked then unchecked, it needs to be manually set back to false
            // in order to remove the optional label on a now required field
            setEmailOptional(false);
        }
        setUsePrimaryRegUi(
            isChildParticipant ? usePrimaryAddressCheckbox : null,
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isChildParticipant, Is_Nfp]);

    useEffect(() => {
        setAddressUi(
            Use_Primary_Address ? (
                <div className="col col--12 col--md-6">
                    <ConnectedMailingAddressPreview />
                </div>
            ) : (
                addressFields
            ),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [Use_Primary_Address, regions]);

    useEffect(() => {
        setPhoneUi(
            Use_Primary_Phone ? (
                <>
                    {usePrimaryPhoneCheckbox}
                    <div className="col col--12 col--md-6">
                        <ConnectedPhonePreview />
                    </div>
                </>
            ) : (
                phoneField
            ),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [Use_Primary_Phone, Country]);

    return (
        <div className="grid">
            <div className="col col--12">
                <h2>{heading}</h2>
                {subheading && <p>{subheading}</p>}
            </div>
            <div className="col col--12 col--md-6">
                <FormikField
                    label={firstNameLabel}
                    name="First_Name"
                    type="text"
                    id="First-Name"
                    ariaRequired="true"
                    autoComplete="given-name"
                />
            </div>
            <div className="col col--12 col--md-6">
                <FormikField
                    label={lastNameLabel}
                    name="Last_Name"
                    type="text"
                    id="Last-Name"
                    ariaRequired="true"
                    autoComplete="family-name"
                />
            </div>
            {!isManagedByPrimary && (
                <div className="col col--12">
                    <ConnectedEmailRegistrationField
                        emailDescriptionText={emailDescriptionText}
                        emailLabel={emailLabel}
                        emailOptional={emailOptional}
                    />
                </div>
            )}

            {usePrimaryRegUi}
            {addressUi}
            {shouldDisplayPhone && phoneUi}
        </div>
    );
};

ParticipantDetails.propTypes = {
    sectionHeading: PropTypes.string.isRequired,
    sectionHeadingForAdditionalParticipants: PropTypes.string.isRequired,
    additionalText: PropTypes.string,
    additionalTextForAdditionalParticipants: PropTypes.string.isRequired,
    firstNameLabel: PropTypes.string.isRequired,
    lastNameLabel: PropTypes.string.isRequired,
    emailLabel: PropTypes.string.isRequired,
    emailDescription: PropTypes.string.isRequired,
    emailDescriptionForAdditionalParticipant: PropTypes.string.isRequired,
    emailDescriptionForAdditionalNonFundraisingParticipant:
        PropTypes.string.isRequired,
    emailDescriptionForNonFundraisingParticipant: PropTypes.string.isRequired,
    isChildParticipant: PropTypes.bool.isRequired,
    isManagedByPrimary: PropTypes.bool.isRequired,
    addressLine1Label: PropTypes.string.isRequired,
    addressLine2Label: PropTypes.string.isRequired,
    cityLabel: PropTypes.string.isRequired,
    stateLabel: PropTypes.string.isRequired,
    zipCodeLabel: PropTypes.string.isRequired,
    countryLabel: PropTypes.string.isRequired,
    phoneNumberLabel: PropTypes.string.isRequired,
    phoneInputOptional: PropTypes.string.isRequired,
    shouldDisplayPhone: PropTypes.bool.isRequired,
    phoneNumberCheckboxLabelForAdditionalParticipants:
        PropTypes.string.isRequired,
    addressCheckboxLabelForAdditionalParticipants: PropTypes.string.isRequired,
};

export default ParticipantDetails;
